/**
 * HTTP-Client der SPA. Jede Antwort wird gegen den Vertrag (`shared/contract.ts`)
 * geparst; Mandantenpfade werden nur aus validierten Kennungen gebaut.
 */
import type { z } from 'zod';
import {
  ApiError,
  EbayCompleteResponse,
  EbayResumeResponse,
  PauseLiftResponse,
  EbayConnectResponse,
  ItemOverwriteResponse,
  ItemsResponse,
  LiveUpdateItemsResponse,
  LiveUpdateOptInResponse,
  MeResponse,
  StatusResponse,
  StoreSyncRequestView,
  type EbayCompleteRequest,
  type ItemsQuery,
  ItemBackupsResponse,
  ItemPreviewResponse,
  LiveBlockPreviewResponse,
} from '../../../shared/contract.js';
import { DesignExportResponse, DesignExportSettingsResponse, type DesignExportSettings } from '../../../shared/design-export.js';
import {
  ArticleCreateResponse,
  ArticleDetailResponse,
  ArticleItemsChangeResponse,
  ArticleItemsResponse,
  ArticlePublishResponse,
  ArticleItemOrderResponse,
  ArticleExportResponse,
  ArticleSkuAssignResponse,
  ReviseState,
  ArticlesResponse,
  ArticleBulkDeleteResponse,
  ArticleDeleteResponse,
  ArticleGroupSetResponse,
  ArticleGroupsResponse,
  ArticleGroupNamesResponse,
  ArticleGroupView,
  type ArticleGroupCreateRequest,
  type ArticleGroupRenameRequest,
  type ArticleGroupSetRequest,
  type ArticleBulkDeleteRequest,
  type ArticleOriginFilter,
  DesignsResponse,
  MediaResponse,
  type ArticleCopyRequest,
  ArticleCreateRequest,
  type ArticleItemsRequest,
  type ArticleSaveRequest,
} from '../../../shared/articles.js';
import {
  DesignCreateResponse,
  DesignDeleteResponse,
  DesignEditorResponse,
  DesignPublishResponse,
  DesignTemplatesResponse,
  type DesignApplyTemplateRequest,
  type DesignCopyRequest,
  type DesignCreateRequest,
  type DesignSaveRequest,
} from '../../../shared/designs.js';
import {
  GalleriesResponse,
  GalleryCreateResponse,
  GalleryDeleteResponse,
  GalleryDetailResponse,
  GalleryItemsResponse,
  GalleryUsageResponse,
  MatchResponse,
  GalleryPreviewResponse,
  type GalleryCreateRequest,
  type GallerySaveRequest,
  type RulesDraft,
  type RulesSaveRequest,
} from '../../../shared/galleries.js';
import {
  MediaListResponse,
  MediaUploadStatusResponse,
  MediaUploadTicket,
  UPLOAD_STATUS_MAX,
  type MediaFolderChoice,
  type MediaUploadRequest,
} from '../../../shared/media.js';
import {
  AccountResponse,
  INVOICE_NO,
  InvoicesResponse,
  InvoiceUrlResponse,
  OpenInvoicesResponse,
  MembersResponse,
  type AccountContactUpdate,
} from '../../../shared/account.js';
import { z as zod } from 'zod';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** Deutsche Meldung der API, für Anzeige gedacht. */
    readonly detail?: string,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiRequestError';
  }
}

/** Standardregel der Abfragen (`main.tsx`): Berechtigungs- und Eingabefehler nicht wiederholen, sonst bis zu zweimal. */
export function retryQuery(count: number, error: unknown): boolean {
  return !(error instanceof ApiRequestError && error.status >= 400 && error.status < 500) && count < 2;
}

/** Upload-Kontingent erschöpft (429 `quota`); eigener Hinweis statt allgemeinem Fehler, kein Wiederholen. */
export function isQuotaError(error: unknown): boolean {
  return error instanceof ApiRequestError && (error.status === 429 || error.code === 'quota');
}

/** Datenweg in dieser Umgebung noch nicht vorhanden (503 `unavailable`): bleibt so, kein Fehler zum Wiederholen. */
export function isUnavailableError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 503 && error.code === 'unavailable';
}

export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken(): Promise<string | undefined>;
  onUnauthorized(): void;
  fetch?: typeof fetch;
}

export type ItemsParams = Partial<Omit<z.input<typeof ItemsQuery>, 'limit'>> & { limit?: number };

function tenantPath(tenantId: string): string {
  if (!/^[1-9][0-9]{0,19}$/.test(tenantId)) throw new ApiRequestError(400, 'invalid-request');
  return `/tenants/${tenantId}`;
}

/** Artikel- und Design-IDs landen im Pfad: nur einfache Zeichen. */
function idPath(id: string): string {
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(id)) throw new ApiRequestError(400, 'invalid-request');
  return id;
}

/** Version als Abfrageparameter: nur nichtnegative Ganzzahlen. */
function versionParam(version: number): string {
  if (!Number.isSafeInteger(version) || version < 0) throw new ApiRequestError(400, 'invalid-request');
  return String(version);
}

/** eBay-Angebotsnummer im Pfad: nur Dezimalziffern (core `EbayItemId`). */
function itemPath(ebayItemId: string): string {
  if (!/^[1-9][0-9]{0,19}$/.test(ebayItemId)) throw new ApiRequestError(400, 'invalid-request');
  return ebayItemId;
}

function galleryPath(galleryId: string): string {
  if (!/^[A-Za-z0-9_-]{1,40}$/.test(galleryId)) throw new ApiRequestError(400, 'invalid-request');
  return galleryId;
}

/** Rechnungsnummer im Pfad: nur das Altformat `P<yy>-<n>`. */
function invoicePath(invoiceNo: string): string {
  if (!INVOICE_NO.test(invoiceNo)) throw new ApiRequestError(400, 'invalid-request');
  return invoiceNo;
}

export function createApiClient(options: ApiClientOptions) {
  const doFetch = options.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));

  async function get<S extends z.ZodTypeAny>(path: string, schema: S): Promise<z.infer<S>> {
    return request(path, schema, { method: 'GET' });
  }

  async function post<S extends z.ZodTypeAny>(path: string, schema: S, body: unknown = {}): Promise<z.infer<S>> {
    return request(path, schema, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } });
  }

  async function put<S extends z.ZodTypeAny>(path: string, schema: S, body: unknown): Promise<z.infer<S>> {
    return request(path, schema, { method: 'PUT', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } });
  }

  async function del<S extends z.ZodTypeAny>(path: string, schema: S): Promise<z.infer<S>> {
    return request(path, schema, { method: 'DELETE' });
  }

  async function request<S extends z.ZodTypeAny>(path: string, schema: S, init: RequestInit): Promise<z.infer<S>> {
    let token: string | undefined;
    try {
      token = await options.getAccessToken();
    } catch {
      throw new ApiRequestError(0, 'network'); // Sitzung bleibt bestehen, Abfrage darf wiederholen
    }
    if (!token) {
      options.onUnauthorized();
      throw new ApiRequestError(401, 'unauthorized');
    }
    let res: Response;
    try {
      res = await doFetch(`${options.baseUrl}${path}`, {
        ...init,
        headers: { ...(init.headers as Record<string, string> | undefined), Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
    } catch {
      throw new ApiRequestError(0, 'network');
    }
    const body: unknown = await res.json().catch(() => undefined);
    if (!res.ok) {
      if (res.status === 401) options.onUnauthorized();
      const parsed = ApiError.safeParse(body);
      throw new ApiRequestError(res.status, parsed.success ? parsed.data.error.code : 'unknown', parsed.success ? parsed.data.error.message : undefined);
    }
    return schema.parse(body);
  }

  return {
    me: () => get('/me', MeResponse),
    status: (tenantId: string) => get(`${tenantPath(tenantId)}/status`, StatusResponse),
    requestStoreSync: (tenantId: string) => post(`${tenantPath(tenantId)}/sync/store/request`, StoreSyncRequestView, {}),
    /** Mitglieder des Kontos (H.1b): nur Rolle, Beginn und „Sie“. */
    members: (tenantId: string) => get(`${tenantPath(tenantId)}/members`, MembersResponse),
    /** Kundendaten (H.2, nur Inhaber); ohne `profile`, solange nichts importiert ist. */
    account: (tenantId: string) => get(`${tenantPath(tenantId)}/account`, AccountResponse),
    /** Nur der Kontaktteil, gegen `expectedVersion`; Konflikt → 409. */
    updateAccountContact: (tenantId: string, body: AccountContactUpdate) => put(`${tenantPath(tenantId)}/account`, AccountResponse, body),
    /** Rechnungen, neueste zuerst; `cursor` aus der vorigen Seite. */
    invoices(tenantId: string, params: { cursor?: string | undefined; limit?: number } = {}) {
      const search = new URLSearchParams();
      if (params.cursor) search.set('cursor', params.cursor);
      if (params.limit !== undefined) search.set('limit', String(params.limit));
      const query = search.toString();
      return get(`${tenantPath(tenantId)}/invoices${query ? `?${query}` : ''}`, InvoicesResponse);
    },
    /** Offene Rechnungen (Regel des Altsystems) für den Hinweis auf der Startseite; nur Inhaber. */
    openInvoices: (tenantId: string) => get(`${tenantPath(tenantId)}/invoices/open`, OpenInvoicesResponse),
    /** Kurzlebige signierte Adresse zum PDF; die SPA ruft sie nie selbst ab, sondern öffnet sie. */
    invoiceUrl: (tenantId: string, invoiceNo: string) => get(`${tenantPath(tenantId)}/invoices/${invoicePath(invoiceNo)}/url`, InvoiceUrlResponse),
    items(tenantId: string, params: ItemsParams) {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== '') search.set(key, String(value));
      }
      const query = search.toString();
      return get(`${tenantPath(tenantId)}/items${query ? `?${query}` : ''}`, ItemsResponse);
    },
    /** „Trotzdem überschreiben“ für genau den angezeigten Stand (`divergedAt`) eines bei eBay geänderten Angebots. */
    overwriteDescription: (tenantId: string, ebayItemId: string, divergedAt: string) =>
      post(`${tenantPath(tenantId)}/items/${itemPath(ebayItemId)}/overwrite`, ItemOverwriteResponse, { divergedAt }),
    ebayConnect: (tenantId: string) => post(`${tenantPath(tenantId)}/ebay/connect`, EbayConnectResponse),
    ebayComplete: (tenantId: string, body: Partial<EbayCompleteRequest> = {}) =>
      post(`${tenantPath(tenantId)}/ebay/complete`, EbayCompleteResponse, body),
    ebayCancel: (tenantId: string) => post(`${tenantPath(tenantId)}/ebay/cancel`, zod.object({ ok: zod.boolean() })),
    ebayResume: (tenantId: string) => post(`${tenantPath(tenantId)}/ebay/resume`, EbayResumeResponse),
    liftPause: (tenantId: string, pausedAt: string) => post(`${tenantPath(tenantId)}/sync/lift-pause`, PauseLiftResponse, { pausedAt }),
    galleries: (tenantId: string) => get(`${tenantPath(tenantId)}/galleries`, GalleriesResponse),
    gallery: (tenantId: string, galleryId: string) => get(`${tenantPath(tenantId)}/galleries/${galleryPath(galleryId)}`, GalleryDetailResponse),
    /** Vorschau und HTML-Code ohne Regeln (beliebige aktive Angebote, wie Legacy ohne Ausgangsangebot); `box`: Code für eine Designbox (`nc=1`). */
    galleryPreview: (tenantId: string, galleryId: string, box = false) =>
      get(`${tenantPath(tenantId)}/galleries/${galleryPath(galleryId)}/preview${box ? '?box=1' : ''}`, GalleryPreviewResponse),
    match: (tenantId: string, galleryId: string, sourceItemId?: string, draft?: RulesDraft) =>
      post(`${tenantPath(tenantId)}/galleries/${galleryPath(galleryId)}/rules/match`, MatchResponse, {
        ...(sourceItemId ? { sourceItemId } : {}),
        ...(draft ? { draft } : {}),
      }),
    /** Artikelliste seitenweise; Suche und Herkunft in der API, `cursor` aus der vorigen Seite. */
    articles(tenantId: string, params: { q?: string; origin?: ArticleOriginFilter; group?: string; cursor?: string | undefined; limit?: number } = {}) {
      const search = new URLSearchParams();
      if (params.q) search.set('q', params.q);
      if (params.origin && params.origin !== 'all') search.set('origin', params.origin);
      if (params.group && params.group !== 'all') search.set('group', params.group);
      if (params.cursor) search.set('cursor', params.cursor);
      if (params.limit !== undefined) search.set('limit', String(params.limit));
      const query = search.toString();
      return get(`${tenantPath(tenantId)}/articles${query ? `?${query}` : ''}`, ArticlesResponse);
    },
    article: (tenantId: string, articleId: string) => get(`${tenantPath(tenantId)}/articles/${idPath(articleId)}`, ArticleDetailResponse),
    /** Artikelgruppen mit Artikelzahl; anlegen (Kennung aus der SPA), umbenennen und löschen mit `updatedAt`. */
    articleGroups: (tenantId: string) => get(`${tenantPath(tenantId)}/article-groups`, ArticleGroupsResponse),
    /** Nur Namen, ohne Zähler (liest keine Artikelköpfe). */
    articleGroupNames: (tenantId: string) => get(`${tenantPath(tenantId)}/article-groups?counts=false`, ArticleGroupNamesResponse),
    createArticleGroup: (tenantId: string, body: ArticleGroupCreateRequest) => post(`${tenantPath(tenantId)}/article-groups`, ArticleGroupView, body),
    renameArticleGroup: (tenantId: string, groupId: string, body: ArticleGroupRenameRequest) =>
      put(`${tenantPath(tenantId)}/article-groups/${idPath(groupId)}`, ArticleGroupView, body),
    deleteArticleGroup: (tenantId: string, groupId: string, expectedUpdatedAt: string) =>
      del(`${tenantPath(tenantId)}/article-groups/${idPath(groupId)}?expectedUpdatedAt=${encodeURIComponent(expectedUpdatedAt)}`, zod.object({ ok: zod.boolean() })),
    /** Gruppe am Artikel; ändert weder Version noch Veröffentlichungsstand. */
    setArticleGroup: (tenantId: string, articleId: string, body: ArticleGroupSetRequest) =>
      put(`${tenantPath(tenantId)}/articles/${idPath(articleId)}/group`, ArticleGroupSetResponse, body),
    /** Eine Welle von „Alle App-2-Artikel löschen“; weiter mit `after = next`, bis `next` fehlt oder `stopped` gesetzt ist. */
    deleteApp2Articles: (tenantId: string, body: ArticleBulkDeleteRequest) => post(`${tenantPath(tenantId)}/articles/app2/delete`, ArticleBulkDeleteResponse, body),
    createArticle: (tenantId: string, body: ArticleCreateRequest) => post(`${tenantPath(tenantId)}/articles`, ArticleCreateResponse, body),
    /** `unassignItems`: vom Kunden bestätigt, Zuordnungen zu Angeboten vorher lösen. */
    deleteArticle: (tenantId: string, articleId: string, expectedVersion: number, unassignItems = false) =>
      del(`${tenantPath(tenantId)}/articles/${idPath(articleId)}?expectedVersion=${versionParam(expectedVersion)}${unassignItems ? '&unassignItems=1' : ''}`, ArticleDeleteResponse),
    copyArticle: (tenantId: string, articleId: string, body: ArticleCopyRequest) =>
      post(`${tenantPath(tenantId)}/articles/${idPath(articleId)}/copy`, ArticleCreateResponse, body),
    saveArticle: (tenantId: string, articleId: string, body: ArticleSaveRequest) =>
      put(`${tenantPath(tenantId)}/articles/${idPath(articleId)}`, ArticleDetailResponse, body),
    publishArticle: (tenantId: string, articleId: string, expectedVersion: number) =>
      post(`${tenantPath(tenantId)}/articles/${idPath(articleId)}/publish`, ArticlePublishResponse, { expectedVersion }),
    /** „HTML kopieren“: Beschreibungscode der veröffentlichten Fassung (nur lesen). */
    exportArticle: (tenantId: string, articleId: string, variant: 'full' | 'description' = 'full') =>
      get(`${tenantPath(tenantId)}/articles/${idPath(articleId)}/export?variant=${variant}`, ArticleExportResponse),
    /** Schreibauftrag für genau ein Angebot (`customer-ordered`); vorher bestätigt der Kunde ausdrücklich. */
    orderItemDescription: (tenantId: string, articleId: string, ebayItemId: string, publishedVersion: number) =>
      post(`${tenantPath(tenantId)}/articles/${idPath(articleId)}/items/${idPath(ebayItemId)}/order`, ArticleItemOrderResponse, { publishedVersion }),
    /** Live-Update ein-/ausschalten (Opt-in des Kunden, nur Inhaber). */
    setLiveUpdate: (tenantId: string, enabled: boolean, expectedChangedAt: string | null) =>
      put(`${tenantPath(tenantId)}/live-update`, LiveUpdateOptInResponse, { enabled, expectedChangedAt }),
    /** Angebote ins Live-Update des bisherigen PAT (Flori 02.11.2026), höchstens 50 je Auftrag. */
    queueLiveUpdateItems: (tenantId: string, ebayItemIds: readonly string[]) =>
      post(`${tenantPath(tenantId)}/live-update/items`, LiveUpdateItemsResponse, { ebayItemIds }),
    assignBySku: (tenantId: string, articleId: string) => post(`${tenantPath(tenantId)}/articles/${idPath(articleId)}/items/by-sku`, ArticleSkuAssignResponse),
    articleItems: (tenantId: string, articleId: string) => get(`${tenantPath(tenantId)}/articles/${idPath(articleId)}/items`, ArticleItemsResponse),
    changeArticleItems: (tenantId: string, articleId: string, body: ArticleItemsRequest) =>
      post(`${tenantPath(tenantId)}/articles/${idPath(articleId)}/items`, ArticleItemsChangeResponse, body),
    designs: (tenantId: string) => get(`${tenantPath(tenantId)}/designs`, DesignsResponse),
    media: (tenantId: string, mediaIds: readonly string[]) =>
      get(`${tenantPath(tenantId)}/media?ids=${mediaIds.map(id => idPath(id)).join(',')}`, MediaResponse),
    /** Medienbibliothek je Ordner, neueste zuerst; `cursor` aus der vorigen Seite. */
    mediaLibrary(tenantId: string, params: { folder: MediaFolderChoice; cursor?: string | undefined; limit?: number }) {
      const search = new URLSearchParams({ folder: params.folder });
      if (params.cursor) search.set('cursor', params.cursor);
      if (params.limit !== undefined) search.set('limit', String(params.limit));
      return get(`${tenantPath(tenantId)}/media/library?${search.toString()}`, MediaListResponse);
    },
    /** Upload anlegen (G.2): Ticket für den Formular-Upload in den Staging-Bucket; gleiche `requestId` = derselbe Upload. */
    createMediaUpload: (tenantId: string, body: MediaUploadRequest) => post(`${tenantPath(tenantId)}/media/uploads`, MediaUploadTicket, body),
    /** Stand laufender Uploads (1 bis `UPLOAD_STATUS_MAX` IDs); unbekannte und fremde fehlen in der Antwort. */
    async mediaUploadStatus(tenantId: string, uploadIds: readonly string[]) {
      if (uploadIds.length === 0 || uploadIds.length > UPLOAD_STATUS_MAX) throw new ApiRequestError(400, 'invalid-request');
      return get(`${tenantPath(tenantId)}/media/uploads?ids=${uploadIds.map(id => idPath(id)).join(',')}`, MediaUploadStatusResponse);
    },
    design: (tenantId: string, designId: string) => get(`${tenantPath(tenantId)}/designs/${idPath(designId)}`, DesignEditorResponse),
    designTemplates: (tenantId: string) => get(`${tenantPath(tenantId)}/designs/templates`, DesignTemplatesResponse),
    createDesign: (tenantId: string, body: DesignCreateRequest) => post(`${tenantPath(tenantId)}/designs`, DesignCreateResponse, body),
    /** Design-Export für Fremdsysteme: veröffentlichte Fassung mit Plattform-Platzhaltern (nur lesen). */
    designExportSettings: (tenantId: string, designId: string) =>
      get(`${tenantPath(tenantId)}/designs/${idPath(designId)}/export-settings`, DesignExportSettingsResponse),
    exportDesign: (tenantId: string, designId: string, settings: DesignExportSettings) =>
      post(`${tenantPath(tenantId)}/designs/${idPath(designId)}/export`, DesignExportResponse, { settings }),
    copyDesign: (tenantId: string, designId: string, body: DesignCopyRequest) =>
      post(`${tenantPath(tenantId)}/designs/${idPath(designId)}/copy`, DesignCreateResponse, body),
    saveDesign: (tenantId: string, designId: string, body: DesignSaveRequest) =>
      put(`${tenantPath(tenantId)}/designs/${idPath(designId)}`, DesignEditorResponse, body),
    applyDesignTemplate: (tenantId: string, designId: string, body: DesignApplyTemplateRequest) =>
      post(`${tenantPath(tenantId)}/designs/${idPath(designId)}/apply-template`, DesignEditorResponse, body),
    publishDesign: (tenantId: string, designId: string, expectedVersion: number) =>
      post(`${tenantPath(tenantId)}/designs/${idPath(designId)}/publish`, DesignPublishResponse, { expectedVersion }),
    deleteDesign: (tenantId: string, designId: string, expectedVersion: number) =>
      del(`${tenantPath(tenantId)}/designs/${idPath(designId)}?expectedVersion=${versionParam(expectedVersion)}`, DesignDeleteResponse),
    itemPreview: (tenantId: string, ebayItemId: string) => get(`${tenantPath(tenantId)}/items/${idPath(ebayItemId)}/preview`, ItemPreviewResponse),
    liveBlockPreview: (tenantId: string, ebayItemId: string) => post(`${tenantPath(tenantId)}/items/${idPath(ebayItemId)}/live-preview`, LiveBlockPreviewResponse),
    itemBackups: (tenantId: string, ebayItemId: string) => get(`${tenantPath(tenantId)}/items/${idPath(ebayItemId)}/backups`, ItemBackupsResponse),
    createGallery: (tenantId: string, body: GalleryCreateRequest) => post(`${tenantPath(tenantId)}/galleries`, GalleryCreateResponse, body),
    galleryItems: (tenantId: string, galleryId: string, page: { limit?: number; cursor?: string } = {}) => {
      const params = new URLSearchParams({ ...(page.limit !== undefined ? { limit: String(page.limit) } : {}), ...(page.cursor !== undefined ? { cursor: page.cursor } : {}) });
      const search = params.toString();
      const query = search ? `?${search}` : '';
      return get(`${tenantPath(tenantId)}/galleries/${galleryPath(galleryId)}/items${query}`, GalleryItemsResponse);
    },
    galleryUsage: (tenantId: string, galleryId: string) => get(`${tenantPath(tenantId)}/galleries/${galleryPath(galleryId)}/usage`, GalleryUsageResponse),
    deleteGallery: (tenantId: string, galleryId: string, expectedVersion: number) =>
      del(`${tenantPath(tenantId)}/galleries/${galleryPath(galleryId)}?expectedVersion=${versionParam(expectedVersion)}`, GalleryDeleteResponse),
    saveGallery: (tenantId: string, galleryId: string, body: GallerySaveRequest) =>
      put(`${tenantPath(tenantId)}/galleries/${galleryPath(galleryId)}`, GalleryDetailResponse, body),
    saveRules: (tenantId: string, galleryId: string, body: RulesSaveRequest) =>
      put(`${tenantPath(tenantId)}/galleries/${galleryPath(galleryId)}/rules`, GalleryDetailResponse, body),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
